import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Modal, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import api, { getBusinessDetailsPublic, getPublicCatalog } from '../services/api';
import AuthContext from '../context/AuthContext';
import AppNavbar from '../components/AppNavbar';
import Jdenticon from 'react-jdenticon';
import { ShoppingCart, Plus, Minus, Trash2, ArrowLeft, MessageCircle, CheckCircle } from 'lucide-react';

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatCurrency = (value) =>
    '$' + Math.round(parseFloat(value || 0)).toLocaleString('es-CO');

const cartTotalItems = (cart) =>
    Object.values(cart).reduce((sum, { quantity }) => sum + quantity, 0);

const cartTotal = (cart) =>
    Object.values(cart).reduce(
        (sum, { product, quantity }) => sum + parseFloat(product.unit_price) * quantity,
        0
    );

const buildWhatsAppUrl = (business, cart, orderForm, tableNumber) => {
    const rawNumber = `${business.phone_country_code || ''}${business.phone || ''}`.replace(/\D/g, '');

    const fmtPrice = (value) => '$' + Math.round(value).toLocaleString('es-CO');

    const items = Object.values(cart);
    const lines = items.map(({ product, quantity }) => {
        const subtotal = parseFloat(product.unit_price) * quantity;
        return `  - ${product.name} x${quantity} = ${fmtPrice(subtotal)}`;
    });

    const total = cartTotal(cart);

    let parts = [];

    if (tableNumber) {
        // --- ESCENARIO 1: El cliente está en el local (Mesa) ---
        parts = [
            `¡Hola! Estamos en la mesa *${tableNumber}* y nos gustaría pedir lo siguiente:`,
            '',
            ...lines,
            '',
            `TOTAL: ${fmtPrice(total)}`
        ];

        if (orderForm.name) parts.push('', `A nombre de: ${orderForm.name}`);
        if (orderForm.comments) parts.push(`Comentarios: ${orderForm.comments}`);

    } else {
        // --- ESCENARIO 2: El cliente pide para domicilio o recoger ---
        parts = [
            '¡Hola! Me gustaría hacer un pedido.',
            '',
            `Nombre: ${orderForm.name}`,
            `Celular: ${orderForm.phone}`
        ];

        if (orderForm.address) parts.push(`Dirección: ${orderForm.address}`);
        if (orderForm.comments) parts.push(`Comentarios: ${orderForm.comments}`);

        parts.push(
            '',
            'Con los siguientes productos:',
            '',
            ...lines,
            '',
            `TOTAL: ${fmtPrice(total)}`
        );
    }

    const text = parts.join('\n');
    return `https://wa.me/${rawNumber}?text=${encodeURIComponent(text)}`;
};

// ─── ProductCard ─────────────────────────────────────────────────────────────

const ProductCard = ({ product, quantity, onAdd, onChangeQty }) => {
    const { t } = useTranslation();
    const [imgError, setImgError] = useState(false);
    const [showPreview, setShowPreview] = useState(false);

    return (
        <Card className="h-100 shadow-sm border-0" style={{ borderRadius: 12 }}>
            {product.image_url && !imgError ? (
                <Card.Img
                    variant="top"
                    src={product.image_url}
                    alt={product.name}
                    onError={() => setImgError(true)}
                    onClick={() => setShowPreview(true)}
                    role="button"
                    tabIndex={0}
                    title={t('publicCatalog.expandImage', 'Ver imagen completa')}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setShowPreview(true);
                        }
                    }}
                    style={{ height: 160, objectFit: 'cover', borderRadius: '12px 12px 0 0', cursor: 'pointer' }}
                />
            ) : (
                <div
                    className="d-flex align-items-center justify-content-center bg-light"
                    style={{ height: 160, borderRadius: '12px 12px 0 0', padding: 12 }}
                >
                    <img
                        src="/logo_evazard_lineal.png"
                        alt={product.name}
                        style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                    />
                </div>
            )}
            <Card.Body className="d-flex flex-column p-3">
                <Card.Title className="fs-6 fw-bold mb-1" style={{ lineHeight: 1.3 }}>
                    {product.name}
                </Card.Title>
                {product.description && (
                    <Card.Text
                        className="text-muted small mb-2"
                        style={{
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.4,
                        }}
                    >
                        {product.description}
                    </Card.Text>
                )}
                <div className="mt-auto">
                    <p className="fw-bold mb-2" style={{ color: 'var(--ev-text-brand)', fontSize: '1.05rem' }}>
                        {formatCurrency(product.unit_price)}
                    </p>
                    {quantity === 0 ? (
                        <Button
                            variant="primary"
                            size="sm"
                            className="w-100"
                            style={{ border: 'none', borderRadius: 8 }}
                            onClick={() => onAdd(product)}
                        >
                            {t('publicCatalog.addToCart')}
                        </Button>
                    ) : (
                        <div className="d-flex align-items-center justify-content-between gap-2">
                            <Button
                                variant="outline-primary"
                                size="sm"
                                style={{ borderRadius: 8, minWidth: 36 }}
                                onClick={() => onChangeQty(product.product_id, -1)}
                            >
                                <Minus size={14} />
                            </Button>
                            <span className="fw-bold fs-6">{quantity}</span>
                            <Button
                                variant="primary"
                                size="sm"
                                style={{ border: 'none', borderRadius: 8, minWidth: 36 }}
                                onClick={() => onChangeQty(product.product_id, 1)}
                            >
                                <Plus size={14} />
                            </Button>
                        </div>
                    )}
                </div>
            </Card.Body>

            {product.image_url && !imgError && (
                <Modal show={showPreview} onHide={() => setShowPreview(false)} centered size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title className="fs-6">{product.name}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="text-center p-0">
                        <img
                            src={product.image_url}
                            alt={product.name}
                            style={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }}
                        />
                    </Modal.Body>
                </Modal>
            )}
        </Card>
    );
};

// ─── CartView ────────────────────────────────────────────────────────────────

const CartView = ({ cart, onChangeQty, onRemove, onClear, onBack, onConfirm, isEmployeeMode }) => {
    const { t } = useTranslation();
    const items = Object.values(cart);
    const total = cartTotal(cart);

    return (
        <div>
            <div className="d-flex align-items-center mb-4 gap-3">
                <Button variant="link" className="p-0 text-decoration-none" onClick={onBack}>
                    <ArrowLeft size={20} className="me-1" />
                    {t('publicCatalog.backToCatalog')}
                </Button>
                <h5 className="mb-0 ms-auto">{t('publicCatalog.cartTitle')}</h5>
            </div>

            {items.length === 0 ? (
                <p className="text-muted text-center py-4">{t('publicCatalog.cartEmpty')}</p>
            ) : (
                <>
                    {items.map(({ product, quantity }) => (
                        <div
                            key={product.product_id}
                            className="d-flex align-items-center gap-3 py-3 border-bottom"
                        >
                            <div className="flex-grow-1">
                                <div className="fw-semibold">{product.name}</div>
                                <div className="text-muted small">
                                    {formatCurrency(product.unit_price)} × {quantity} = {formatCurrency(parseFloat(product.unit_price) * quantity)}
                                </div>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    style={{ borderRadius: 8, minWidth: 32 }}
                                    onClick={() => onChangeQty(product.product_id, -1)}
                                >
                                    <Minus size={12} />
                                </Button>
                                <span className="fw-bold">{quantity}</span>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    style={{ borderRadius: 8, minWidth: 32 }}
                                    onClick={() => onChangeQty(product.product_id, 1)}
                                >
                                    <Plus size={12} />
                                </Button>
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    style={{ borderRadius: 8, minWidth: 32 }}
                                    onClick={() => onRemove(product.product_id)}
                                >
                                    <Trash2 size={12} />
                                </Button>
                            </div>
                        </div>
                    ))}

                    <div className="d-flex justify-content-between align-items-center mt-3 pt-2">
                        <span className="fw-bold fs-5">{t('publicCatalog.total')}</span>
                        <span className="fw-bold fs-5" style={{ color: 'var(--ev-text-brand)' }}>
                            {formatCurrency(total)}
                        </span>
                    </div>

                    <div className="d-flex gap-2 mt-4">
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={onClear}
                        >
                            {t('publicCatalog.clearCart')}
                        </Button>
                        <Button
                            variant="success"
                            className="flex-grow-1"
                            style={{ borderRadius: 8 }}
                            onClick={onConfirm}
                        >
                            {isEmployeeMode
                                ? <><CheckCircle size={16} className="me-2" />{t('publicCatalog.confirmOrderEmployee')}</>
                                : <><MessageCircle size={16} className="me-2" />{t('publicCatalog.confirmOrder')}</>
                            }
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};

// ─── OrderModal (WhatsApp – clientes externos) ────────────────────────────────

const OrderModal = ({ show, onHide, business, cart, tableNumber }) => {
    const { t } = useTranslation();
    const isMesa = !!tableNumber;
    const [form, setForm] = useState({ name: '', phone: '', address: '', comments: '' });
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const newErrors = {};
        if (!isMesa) {
            if (!form.name.trim()) newErrors.name = t('publicCatalog.orderModal.nameRequired');
            if (!form.phone.trim()) newErrors.phone = t('publicCatalog.orderModal.phoneRequired');
        }
        return newErrors;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const newErrors = validate();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        const url = buildWhatsAppUrl(business, cart, form, tableNumber);
        window.open(url, '_blank');
        onHide();
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    {isMesa
                        ? t('publicCatalog.orderModal.titleMesa', { table: tableNumber })
                        : t('publicCatalog.orderModal.title')}
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {isMesa ? (
                        <>
                            <p className="text-muted small mb-3">
                                {t('publicCatalog.orderModal.mesaHint', { table: tableNumber })}
                            </p>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('publicCatalog.orderModal.nameOptionalLabel')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    value={form.name}
                                    onChange={handleChange}
                                    autoComplete="name"
                                    placeholder={t('publicCatalog.orderModal.namePlaceholder')}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('publicCatalog.orderModal.commentsLabel')}</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    name="comments"
                                    value={form.comments}
                                    onChange={handleChange}
                                    placeholder={t('publicCatalog.orderModal.commentsPlaceholder')}
                                />
                            </Form.Group>
                        </>
                    ) : (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('publicCatalog.orderModal.nameLabel')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    value={form.name}
                                    onChange={handleChange}
                                    isInvalid={!!errors.name}
                                    autoComplete="name"
                                />
                                <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('publicCatalog.orderModal.phoneLabel')}</Form.Label>
                                <Form.Control
                                    type="tel"
                                    name="phone"
                                    value={form.phone}
                                    onChange={handleChange}
                                    isInvalid={!!errors.phone}
                                    autoComplete="tel"
                                />
                                <Form.Control.Feedback type="invalid">{errors.phone}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('publicCatalog.orderModal.addressLabel')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="address"
                                    value={form.address}
                                    onChange={handleChange}
                                    autoComplete="street-address"
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('publicCatalog.orderModal.commentsLabel')}</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    name="comments"
                                    value={form.comments}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>{t('cancel', 'Cancelar')}</Button>
                    <Button
                        variant="success"
                        type="submit"
                        style={{ borderRadius: 8 }}
                    >
                        <MessageCircle size={16} className="me-2" />
                        {t('publicCatalog.orderModal.submitButton')}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

// ─── EmployeeObservationsModal ────────────────────────────────────────────────

const EmployeeObservationsModal = ({ show, onHide, onConfirm, submitting }) => {
    const { t } = useTranslation();
    const [observations, setObservations] = useState('');

    useEffect(() => {
        if (show) setObservations('');
    }, [show]);

    const handleSubmit = (e) => {
        e.preventDefault();
        onConfirm(observations);
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>{t('publicCatalog.employeeModal.title')}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <p className="text-muted small">{t('publicCatalog.employeeModal.hint')}</p>
                    <Form.Group>
                        <Form.Label>{t('publicCatalog.orderModal.commentsLabel')}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={observations}
                            onChange={e => setObservations(e.target.value)}
                            placeholder={t('publicCatalog.orderModal.commentsPlaceholder')}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={submitting}>{t('cancel', 'Cancelar')}</Button>
                    <Button variant="success" type="submit" disabled={submitting}>
                        {submitting
                            ? <Spinner size="sm" animation="border" className="me-1" />
                            : <CheckCircle size={16} className="me-1" />
                        }
                        {t('publicCatalog.employeeModal.submit')}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

// ─── PublicCatalogPage ───────────────────────────────────────────────────────

// Evita prefijos redundantes: si el nombre ya empieza con "mesa" no agrega "Mesa: "
const formatTableLabel = (name) => {
    if (!name) return '';
    return /^mesa\b/i.test(name.trim()) ? name : `Mesa: ${name}`;
};

const PublicCatalogPage = () => {
    const { businessSlug } = useParams();
    const [searchParams] = useSearchParams();
    const tableNumber = searchParams.get('table');    // display name (e.g. "Mesa 1")
    const tableId = searchParams.get('table_id');     // DB id (e.g. "3")
    const employeeParam = searchParams.get('employee'); // "true" for quick-sale without table
    const { t } = useTranslation();

    // Detect employee/collaborator mode: user is authenticated + belongs to this business
    const { user, business: authBusiness } = useContext(AuthContext);
    const isEmployeeMode = !!(
        user &&
        authBusiness &&
        (tableId || employeeParam === 'true') &&
        authBusiness.slug === businessSlug
    );

    const [business, setBusiness] = useState(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [cart, setCart] = useState({});
    const [view, setView] = useState('catalog'); // 'catalog' | 'cart'
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [showEmployeeModal, setShowEmployeeModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTag, setSelectedTag] = useState(null);

    const allTags = useMemo(
        () => [...new Set(products.flatMap(p => p.tags || []))].sort(),
        [products]
    );

    const filteredProducts = useMemo(() => {
        const term = searchTerm.toLowerCase().trim();
        return products.filter(p => {
            const matchesSearch = !term ||
                p.name.toLowerCase().includes(term) ||
                (p.description || '').toLowerCase().includes(term);
            const matchesTag = !selectedTag || (p.tags || []).includes(selectedTag);
            return matchesSearch && matchesTag;
        });
    }, [products, searchTerm, selectedTag]);

    const toggleTag = useCallback((tag) => {
        setSelectedTag(prev => prev === tag ? null : tag);
    }, []);

    useEffect(() => {
        const load = async () => {
            try {
                const bizRes = await getBusinessDetailsPublic(businessSlug);
                setBusiness(bizRes.data);
            } catch (e) {
                setError(t('publicCatalog.errors.loadFailed'));
                setLoading(false);
                return;
            }
            try {
                const catRes = await getPublicCatalog(businessSlug);
                setProducts(catRes.data.products);
            } catch (e) {
                console.error('Error loading catalog products:', e);
            }
            setLoading(false);
        };
        load();
    }, [businessSlug, t]);

    const addToCart = useCallback((product) => {
        setCart(prev => {
            const existing = prev[product.product_id];
            return {
                ...prev,
                [product.product_id]: {
                    product,
                    quantity: existing ? existing.quantity + 1 : 1
                }
            };
        });
    }, []);

    const changeQty = useCallback((productId, delta) => {
        setCart(prev => {
            const item = prev[productId];
            if (!item) return prev;
            const newQty = item.quantity + delta;
            if (newQty <= 0) {
                const next = { ...prev };
                delete next[productId];
                return next;
            }
            return { ...prev, [productId]: { ...item, quantity: newQty } };
        });
    }, []);

    const removeFromCart = useCallback((productId) => {
        setCart(prev => {
            const next = { ...prev };
            delete next[productId];
            return next;
        });
    }, []);

    const clearCart = useCallback(() => setCart({}), []);

    // ── Employee order submission (PPC-07) ───────────────────────────────────

    const handleEmployeeConfirm = useCallback(async (observations) => {
        setSubmitting(true);
        try {
            const total = cartTotal(cart);
            const products_payload = Object.values(cart).map(({ product, quantity }) => ({
                product_name: product.name,
                quantity,
                unit_price: product.unit_price,
            }));

            // Build client_name: use table name or "Venta Rápida"
            const clientName = tableNumber
                ? formatTableLabel(tableNumber)
                : t('publicCatalog.quickSaleLabel');

            await api.post('/sales', {
                client_name: clientName,
                total_amount: total,
                paid_amount: 0,
                payment_method: 'Cash',
                products: products_payload,
                observations: observations || null,
                table_id: tableId ? parseInt(tableId, 10) : null,
                is_layaway: false,
                client_generated_id: crypto.randomUUID(),
            });

            // PPC-08: Reset cart + feedback
            clearCart();
            setView('catalog');
            setShowEmployeeModal(false);
            toast.success(t('publicCatalog.orderSentSuccess'));
        } catch (err) {
            const msg = err.response?.data?.message || t('publicCatalog.orderSentError');
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    }, [cart, tableId, tableNumber, clearCart, t]);

    const handleConfirmClick = useCallback(() => {
        if (isEmployeeMode) {
            setShowEmployeeModal(true);
        } else {
            setShowOrderModal(true);
        }
    }, [isEmployeeMode]);

    const totalItems = cartTotalItems(cart);
    const total = cartTotal(cart);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
                <Spinner animation="border" style={{ color: 'var(--ev-text-brand)' }} />
            </div>
        );
    }

    if (error) {
        return (
            <Container className="py-5">
                <Alert variant="danger">{error}</Alert>
            </Container>
        );
    }

    return (
        <div style={{ minHeight: '100vh', backgroundColor: 'var(--ev-bg-canvas)', paddingBottom: totalItems > 0 ? 90 : 24 }}>
            {/* Navbar completo para empleados autenticados */}
            {isEmployeeMode && <AppNavbar />}

            {/* Header del catálogo — solo para clientes externos (sin sesión) */}
            {!isEmployeeMode && (
            <div
                className="sticky-top bg-white shadow-sm py-3 px-3"
                style={{ zIndex: 100 }}
            >
                <div className="d-flex align-items-center gap-3" style={{ maxWidth: 900, margin: '0 auto' }}>
                    {/* Logo del negocio: imagen si existe, Jdenticon como fallback */}
                    {business?.logo_url ? (
                        <img
                            src={business.logo_url}
                            alt={business.name}
                            style={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 8 }}
                        />
                    ) : business?.name ? (
                        <div style={{ width: 44, height: 44, borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}>
                            <Jdenticon value={business.name} size="44" />
                        </div>
                    ) : null}
                    <div className="flex-grow-1">
                        <h5 className="mb-0 fw-bold">{business?.name}</h5>
                        {tableNumber && (
                            <small className="text-muted">{formatTableLabel(tableNumber)}</small>
                        )}
                    </div>
                    {view === 'catalog' && totalItems > 0 && (
                        <Button
                            variant="outline-primary"
                            size="sm"
                            style={{ borderRadius: 20, position: 'relative' }}
                            onClick={() => setView('cart')}
                        >
                            <ShoppingCart size={16} className="me-1" />
                            {t('publicCatalog.viewCart', { count: totalItems })}
                            <Badge
                                bg="danger"
                                pill
                                style={{ position: 'absolute', top: -6, right: -6, fontSize: '0.65rem' }}
                            >
                                {totalItems}
                            </Badge>
                        </Button>
                    )}
                    {view === 'cart' && (
                        <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-decoration-none"
                            onClick={() => setView('catalog')}
                        >
                            <ArrowLeft size={18} className="me-1" />
                            {t('publicCatalog.backToCatalog')}
                        </Button>
                    )}
                </div>
            </div>
            )}

            {/* Barra de contexto para empleados (mesa activa + carrito) */}
            {isEmployeeMode && (
            <div className="bg-white border-bottom py-2 px-3" style={{ position: 'sticky', top: 56, zIndex: 99 }}>
                <div className="d-flex align-items-center gap-3" style={{ maxWidth: 900, margin: '0 auto' }}>
                    <div className="flex-grow-1">
                        {tableNumber
                            ? <span className="fw-semibold">{formatTableLabel(tableNumber)}</span>
                            : <span className="text-muted small">{t('publicCatalog.quickSaleLabel')}</span>
                        }
                    </div>
                    {view === 'catalog' && totalItems > 0 && (
                        <Button
                            variant="outline-primary"
                            size="sm"
                            style={{ borderRadius: 20, position: 'relative' }}
                            onClick={() => setView('cart')}
                        >
                            <ShoppingCart size={16} className="me-1" />
                            {t('publicCatalog.viewCart', { count: totalItems })}
                            <Badge
                                bg="danger"
                                pill
                                style={{ position: 'absolute', top: -6, right: -6, fontSize: '0.65rem' }}
                            >
                                {totalItems}
                            </Badge>
                        </Button>
                    )}
                    {view === 'cart' && (
                        <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-decoration-none"
                            onClick={() => setView('catalog')}
                        >
                            <ArrowLeft size={18} className="me-1" />
                            {t('publicCatalog.backToCatalog')}
                        </Button>
                    )}
                </div>
            </div>
            )}

            <Container style={{ maxWidth: 900, padding: '24px 16px' }}>
                {view === 'catalog' && (
                    <>
                        {/* Buscador */}
                        <Form.Control
                            type="search"
                            placeholder={t('publicCatalog.searchPlaceholder')}
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="mb-3"
                            style={{ borderRadius: 10 }}
                        />

                        {/* Chips de etiquetas */}
                        {allTags.length > 0 && (
                            <div className="d-flex flex-wrap gap-2 mb-4">
                                <Button
                                    size="sm"
                                    variant={selectedTag === null ? 'primary' : 'outline-secondary'}
                                    style={{ borderRadius: 20 }}
                                    onClick={() => setSelectedTag(null)}
                                >
                                    {t('publicCatalog.allTags')}
                                </Button>
                                {allTags.map(tag => (
                                    <Button
                                        key={tag}
                                        size="sm"
                                        variant={selectedTag === tag ? 'primary' : 'outline-secondary'}
                                        style={{ borderRadius: 20 }}
                                        onClick={() => toggleTag(tag)}
                                    >
                                        {tag}
                                    </Button>
                                ))}
                            </div>
                        )}

                        {/* Grid de productos */}
                        {products.length === 0 ? (
                            <p className="text-muted text-center py-5">{t('publicCatalog.emptyState')}</p>
                        ) : filteredProducts.length === 0 ? (
                            <p className="text-muted text-center py-5">{t('publicCatalog.noResults')}</p>
                        ) : (
                            <Row xs={1} sm={2} md={3} className="g-3">
                                {filteredProducts.map(product => (
                                    <Col key={product.product_id}>
                                        <ProductCard
                                            product={product}
                                            quantity={cart[product.product_id]?.quantity || 0}
                                            onAdd={addToCart}
                                            onChangeQty={changeQty}
                                        />
                                    </Col>
                                ))}
                            </Row>
                        )}
                    </>
                )}

                {view === 'cart' && (
                    <CartView
                        cart={cart}
                        onChangeQty={changeQty}
                        onRemove={removeFromCart}
                        onClear={clearCart}
                        onBack={() => setView('catalog')}
                        onConfirm={handleConfirmClick}
                        isEmployeeMode={isEmployeeMode}
                    />
                )}
            </Container>

            {/* Branding */}
            <div
                className="text-center py-3"
                style={{ borderTop: '1px solid var(--ev-border-subtle)', color: 'var(--ev-text-muted)', fontSize: '0.75rem' }}
            >
                {t('publicCatalog.poweredBy')}{' '}
                <a
                    href="https://evazard.cvalenciait.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--ev-text-muted)', textDecoration: 'none', fontWeight: 600 }}
                >
                    Evazard
                </a>
            </div>

            {/* Floating bottom bar */}
            {view === 'catalog' && totalItems > 0 && (
                <div
                    className="fixed-bottom bg-white border-top py-3 px-3 d-flex gap-2"
                    style={{ zIndex: 200, boxShadow: '0 -4px 12px rgba(0,0,0,0.08)' }}
                >
                    <div style={{ maxWidth: 900, margin: '0 auto', width: '100%', display: 'flex', gap: 12 }}>
                        <Button
                            variant="outline-primary"
                            className="flex-grow-1"
                            style={{ borderRadius: 10 }}
                            onClick={() => setView('cart')}
                        >
                            <ShoppingCart size={16} className="me-2" />
                            {t('publicCatalog.viewCart', { count: totalItems })}
                            <span className="ms-2 fw-bold">{formatCurrency(total)}</span>
                        </Button>
                        <Button
                            variant="success"
                            className="flex-grow-1"
                            style={{ borderRadius: 10 }}
                            onClick={handleConfirmClick}
                        >
                            {isEmployeeMode
                                ? <><CheckCircle size={16} className="me-2" />{t('publicCatalog.confirmOrderEmployee')}</>
                                : <><MessageCircle size={16} className="me-2" />{t('publicCatalog.confirmOrder')}</>
                            }
                        </Button>
                    </div>
                </div>
            )}

            {/* WhatsApp Order Modal (clientes externos) */}
            {business && !isEmployeeMode && (
                <OrderModal
                    show={showOrderModal}
                    onHide={() => setShowOrderModal(false)}
                    business={business}
                    cart={cart}
                    tableNumber={tableNumber}
                />
            )}

            {/* Employee Confirmation Modal */}
            {isEmployeeMode && (
                <EmployeeObservationsModal
                    show={showEmployeeModal}
                    onHide={() => setShowEmployeeModal(false)}
                    onConfirm={handleEmployeeConfirm}
                    submitting={submitting}
                />
            )}
        </div>
    );
};

export default PublicCatalogPage;
